import React, { useState, useEffect, useContext } from 'react';
import { apiFetch } from '../api/client';
import { useNotification } from '../components/Notifications';
import { can, Permissions } from '../auth/authorization';
import AuthContext from '../AuthContext';

export default function ManageUsers() {
    const [users, setUsers] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '', address: '', password: '' });
    const [creating, setCreating] = useState(false);
    const [roles] = useState(["SalesRep", "StokrazeUser"]);
    const [selectedRole, setSelectedRole] = useState("SalesRep");
    const [removeCandidate, setRemoveCandidate] = useState(null);
    const [editRoleCandidate, setEditRoleCandidate] = useState(null);
    const [viewUserObj, setViewUserObj] = useState(null);
    const [editUserObj, setEditUserObj] = useState(null);
    const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phoneNumber: '' });
    const notify = useNotification();
    const [planInfo, setPlanInfo] = useState(null);
    const [canCreateUsersByPlan, setCanCreateUsersByPlan] = useState(true);
    const [loading, setLoading] = useState(true);

    const auth = useContext(AuthContext);
    const currentUserId = auth?.user || null;

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchUsers(), fetchPlanInfo()]).finally(() => setLoading(false));
    }, []);

    async function fetchPlanInfo() {
        try {
            const res = await apiFetch('/api/subscription');
            if (!res.ok) return;
            const data = await res.json().catch(() => null);
            let plan = data?.plan || data?.data?.plan || data?.data || data || null;
            if (!plan) return;
            const slug = (plan.slug || plan.Slug || plan.name || plan.Name || '').toString().toLowerCase();
            setPlanInfo(plan);
            if (slug === 'classic') {
                // classic allows only 1 user — determine current count
                try {
                    const resUsers = await apiFetch('/api/account/users');
                    if (!resUsers.ok) {
                        setCanCreateUsersByPlan(true);
                        return;
                    }
                    const usersData = await resUsers.json().catch(() => null);
                    const arr = Array.isArray(usersData) ? usersData : (usersData?.data || usersData?.Data || []);
                    const count = (arr || []).length;
                    setCanCreateUsersByPlan(count < 1);
                } catch (e) {
                    // on error allow UI (server will enforce)
                    setCanCreateUsersByPlan(true);
                }
            } else {
                setCanCreateUsersByPlan(slug !== 'basic');
            }
        } catch (e) {
            // ignore
        }
    }

    async function fetchUsers() {
        try {
            const res = await apiFetch('/api/account/users');
            if (!res.ok) return;
            const data = await res.json();
            const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
            setUsers(arr || []);
        } catch (e) { console.error(e); }
    }

    const handleChange = (k, v) => setForm({ ...form, [k]: v });

    const createUser = async () => {
        if (!canCreateUsersByPlan) {
            notify('error', { title: 'Upgrade required', text: 'Your plan does not allow creating additional users. Please upgrade your subscription.' });
            return;
        }
        try {
      const payload = {
        FirstName: form.firstName,
        LastName: form.lastName,
        Email: form.email,
        PhoneNumber: form.phoneNumber,
        Address: form.address,
        Password: form.password,
        Role: selectedRole
      };
      // include role in request body (backend prefers body.Role over query param)
      payload.Role = selectedRole;
      const res = await apiFetch(`/api/account/create-user`, { method: 'POST', body: JSON.stringify(payload) });
            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                const note = (parsed && parsed.message) || 'Failed';
                notify('error', note);
                return;
            }
            notify('success', 'User created');
            setShowCreate(false);
            setForm({ firstName: '', lastName: '', email: '', phoneNumber: '', password: '' });
            fetchUsers();
        } catch (e) {
            console.error(e);
            notify('error', 'Network error');
        }
    }

    const addRole = async (userId, role) => {
        try {
            const res = await apiFetch(`/api/account/users/${encodeURIComponent(userId)}/roles?role=${encodeURIComponent(role)}`, { method: 'POST' });
            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                notify('error', (parsed && parsed.message) || 'Failed to add role');
                return;
            }
            notify('success', 'Role added');
            fetchUsers();
        } catch (e) {
            console.error(e);
            notify('error', 'Network error');
        }
    };

  // prompt removal, show confirmation modal
  const removeRole = (userId, role, email) => {
    // If the edit modal is open for this user, perform the confirm inside the modal
    if (editUserObj && editUserObj.id === userId) {
      setEditRoleCandidate({ userId, role, email, action: 'remove' });
      return;
    }

    setRemoveCandidate({ userId, role, email, deleteUser: false });
  };

  const confirmRemoveRole = async () => {
    if (!removeCandidate) return;
    const { userId, role } = removeCandidate;
    try {
      const res = await apiFetch(`/api/account/users/${encodeURIComponent(userId)}/roles?role=${encodeURIComponent(role)}`, { method: 'DELETE' });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        notify('error', (parsed && parsed.message) || 'Failed to remove role');
        return;
      }
      notify('success', 'Role removed');
      fetchUsers();
    } catch (e) {
      console.error(e);
      notify('error', 'Network error');
    } finally {
      setRemoveCandidate(null);
    }
  };

  const confirmEditRole = async () => {
    if (!editRoleCandidate) return;
    const { userId, role, action } = editRoleCandidate;
    try {
      if (action === 'add') {
        const res = await apiFetch(`/api/account/users/${encodeURIComponent(userId)}/roles?role=${encodeURIComponent(role)}`, { method: 'POST' });
        if (!res.ok) {
          const parsed = await res.json().catch(() => null);
          notify('error', (parsed && parsed.message) || 'Failed to add role');
          return;
        }
        notify('success', 'Role added');
      } else if (action === 'remove') {
        const res = await apiFetch(`/api/account/users/${encodeURIComponent(userId)}/roles?role=${encodeURIComponent(role)}`, { method: 'DELETE' });
        if (!res.ok) {
          const parsed = await res.json().catch(() => null);
          notify('error', (parsed && parsed.message) || 'Failed to remove role');
          return;
        }
        notify('success', 'Role removed');
      }

      // Refresh user list and refresh the edit user roles
      await fetchUsers();
      if (editUserObj && editUserObj.id === userId) {
        // Reload the edited user details from list
        const updated = users.find(u => u.id === userId);
        if (updated) {
          setEditUserObj(updated);
        }
      }
    } catch (e) {
      console.error(e);
      notify('error', 'Network error');
    } finally {
      setEditRoleCandidate(null);
    }
  };

  const cancelEditRole = () => setEditRoleCandidate(null);

  const removeRolePrompt = (userId, email) => {
    setRemoveCandidate({ userId, role: null, email, deleteUser: true });
  };

  const confirmDeleteUser = async () => {
    if (!removeCandidate || !removeCandidate.deleteUser) return;
    try {
      const res = await apiFetch(`/api/account/delete/${encodeURIComponent(removeCandidate.userId)}`, { method: 'DELETE' });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        notify('error', (parsed && parsed.message) || 'Failed to delete user');
        return;
      }
      notify('success', 'User deleted');
      fetchUsers();
    } catch (e) {
      console.error(e);
      notify('error', 'Network error');
    } finally {
      setRemoveCandidate(null);
    }
  };

  const viewUser = (u) => {
    setViewUserObj(u);
  };

  const openEditUser = (u) => {
    setEditUserObj(u);
    setEditForm({ firstName: u.firstName || '', lastName: u.lastName || '', phoneNumber: u.phoneNumber || '' });
  };

  const saveEditUser = async () => {
    if (!editUserObj) return;
    try {
      const payload = { FirstName: editForm.firstName, LastName: editForm.lastName, PhoneNumber: editForm.phoneNumber };
      const res = await apiFetch(`/api/account/users/${encodeURIComponent(editUserObj.id)}`, { method: 'PUT', body: JSON.stringify(payload) });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        notify('error', (parsed && parsed.message) || 'Failed to update user');
        return;
      }
      notify('success', 'User updated');
      setEditUserObj(null);
      fetchUsers();
    } catch (e) {
      console.error(e);
      notify('error', 'Network error');
    }
  };

  const cancelRemove = () => setRemoveCandidate(null);

    if (!can(Permissions.Edit) && !can(Permissions.Add)) {
        return <div className="p-4">Not authorized</div>;
    }

    return (
        <div className="col-span-12 lg:col-span-10 flex justify-center">
            <div className="w-11/12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold">Manage Users</h2>
                    {can(Permissions.Add) && (
                        <button className="bg-blue-600 text-white px-3 py-1 rounded" onClick={() => setShowCreate(!showCreate)}>Create User</button>
                    )}
                </div>

                {/* Create user modal */}
                {showCreate && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                        <div className="bg-white p-6 rounded w-full max-w-lg">
                            <h3 className="text-lg font-semibold mb-3">Create User</h3>
                            <div className="grid grid-cols-2 gap-2">
                                <input className="border p-2" placeholder="First name" value={form.firstName} onChange={e => handleChange('firstName', e.target.value)} />
                                <input className="border p-2" placeholder="Last name" value={form.lastName} onChange={e => handleChange('lastName', e.target.value)} />
                                <input className="border p-2 col-span-2" placeholder="Email" value={form.email} onChange={e => handleChange('email', e.target.value)} />
                                <input className="border p-2" placeholder="Phone" value={form.phoneNumber} onChange={e => handleChange('phoneNumber', e.target.value)} />
                                <input className="border p-2" placeholder="Address" value={form.address || ''} onChange={e => handleChange('address', e.target.value)} />
                                <input className="border p-2" placeholder="Password" type="password" value={form.password} onChange={e => handleChange('password', e.target.value)} />
                                <select className="border p-2" value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div className="mt-4 flex justify-end">
                                <button className="bg-gray-300 px-3 py-1 rounded mr-2" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="bg-green-600 text-white px-3 py-1 rounded" onClick={createUser}>Create</button>
                            </div>
                        </div>
                    </div>
                )}

        <div className="bg-white p-4 rounded shadow-sm">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {users
                  .filter(u => u.id !== currentUserId)
                  .map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{u.firstName} {u.lastName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {(u.roles || []).slice(0,3).map(r => (
                          <span key={r} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${r === 'SuperAdmin' ? 'bg-red-100 text-red-800' : r === 'StokrazeUser' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {r}
                          </span>
                        ))}
                        {(u.roles || []).length > 3 && (
                          <div className="text-xs text-gray-500">+{(u.roles || []).length - 3} more</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <button className="bg-gray-200 px-2 py-1 text-xs rounded" onClick={() => viewUser(u)}>View</button>
                        <button className="bg-blue-600 text-white px-2 py-1 text-xs rounded" onClick={() => openEditUser(u)}>Edit</button>
                        <button className="bg-red-600 text-white px-2 py-1 text-xs rounded" onClick={() => removeRolePrompt(u.id, u.email)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>
        {/* Confirm remove modal */}
        {removeCandidate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white p-6 rounded w-full max-w-sm">
              <h3 className="text-lg font-semibold mb-3">Confirm role removal</h3>
              <p className="mb-4">Remove role <strong>{removeCandidate.role}</strong> from user <strong>{removeCandidate.email}</strong>?</p>
              <div className="flex justify-end gap-2">
                <button className="bg-gray-300 px-3 py-1 rounded" onClick={cancelRemove}>Cancel</button>
                <button className="bg-red-600 text-white px-3 py-1 rounded" onClick={confirmRemoveRole}>Remove</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete user confirmation */}
        {removeCandidate && removeCandidate.deleteUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white p-6 rounded w-full max-w-sm">
              <h3 className="text-lg font-semibold mb-3">Confirm delete user</h3>
              <p className="mb-4">Are you sure you want to delete user <strong>{removeCandidate.email}</strong>? This action cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <button className="bg-gray-300 px-3 py-1 rounded" onClick={() => setRemoveCandidate(null)}>Cancel</button>
                <button className="bg-red-600 text-white px-3 py-1 rounded" onClick={confirmDeleteUser}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* View user modal */}
        {viewUserObj && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white p-6 rounded w-full max-w-md">
              <h3 className="text-lg font-semibold mb-3">User Details</h3>
              <div className="mb-4">
                <div><strong>Email:</strong> {viewUserObj.email}</div>
                <div><strong>Name:</strong> {viewUserObj.firstName} {viewUserObj.lastName}</div>
                <div><strong>Roles:</strong> {(viewUserObj.roles || []).join(', ')}</div>
              </div>
              <div className="flex justify-end">
                <button className="bg-gray-300 px-3 py-1 rounded" onClick={() => setViewUserObj(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit user modal */}
        {editUserObj && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white p-6 rounded w-full max-w-lg">
              <h3 className="text-lg font-semibold mb-3">Edit User</h3>
              <div className="grid grid-cols-2 gap-2">
                <input className="border p-2" placeholder="First name" value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} />
                <input className="border p-2" placeholder="Last name" value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} />
                <input className="border p-2 col-span-2" placeholder="Phone" value={editForm.phoneNumber} onChange={e => setEditForm({ ...editForm, phoneNumber: e.target.value })} />
                <div className="col-span-2">
                  <div className="mb-2 font-medium">Roles</div>
                  <div className="flex gap-2 flex-wrap">
                    {(editUserObj.roles || []).map(r => (
                      <span key={r} className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-xs">
                        {r}
                        <button className="ml-2 text-red-600" onClick={() => removeRole(editUserObj.id, r, editUserObj.email)}>x</button>
                      </span>
                    ))}
                    <select defaultValue="" onChange={e => { const v = e.target.value; if (v) { setEditRoleCandidate({ userId: editUserObj.id, role: v, action: 'add' }); } e.target.value = ''; }} className="border rounded px-2 py-1 text-xs">
                      <option value="">Add role...</option>
                      {roles.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>

                  {/* Inline confirm dialog shown inside edit modal when adding/removing role */}
                  {editRoleCandidate && editRoleCandidate.userId === editUserObj.id && (
                    <div className={`mt-3 p-3 rounded shadow-md flex items-start gap-3 transition-opacity duration-200 ${editRoleCandidate.action === 'add' ? 'border-l-4 border-green-500 bg-green-50' : 'border-l-4 border-red-500 bg-red-50'}`}>
                      <div className="flex-shrink-0 mt-1">
                        {editRoleCandidate.action === 'add' ? (
                          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 mb-1">
                          {editRoleCandidate.action === 'add' ? 'Confirm add role' : 'Confirm remove role'}
                        </div>
                        <div className="text-sm text-gray-700">Are you sure you want to <strong className="text-gray-900">{editRoleCandidate.action === 'add' ? 'add' : 'remove'}</strong> the role <strong className="text-gray-900">{editRoleCandidate.role}</strong> {editRoleCandidate.action === 'add' ? 'to' : 'from'} <strong className="text-gray-900">{editUserObj.email}</strong>?</div>
                        <div className="mt-3 flex justify-end gap-2">
                          <button className="bg-white border border-gray-200 px-3 py-1 rounded text-sm hover:bg-gray-50" onClick={cancelEditRole}>Cancel</button>
                          <button className={`${editRoleCandidate.action === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white px-3 py-1 rounded text-sm`} onClick={confirmEditRole}>OK</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button className="bg-gray-300 px-3 py-1 rounded mr-2" onClick={() => setEditUserObj(null)}>Cancel</button>
                <button className="bg-green-600 text-white px-3 py-1 rounded" onClick={saveEditUser}>Save</button>
              </div>
            </div>
          </div>
        )}
            </div>
        </div>
    );
}
